import React, { useEffect, useState } from "react";
import { countries } from "shared/countriesList";
import { FormControl, makeStyles } from "@material-ui/core";
import { v4 as uuidv4 } from "uuid";
import PropTypes from "prop-types";

const initialCountry = countries.find((country) => country.phone === "+994");

const consoleMessages = {
    noOnChangeProvided:
        'Warning: No "onChange" provided to PhoneInput component, so it will change only it\'s local state ',
};

// Changing this parameters will lead to change styles for ALL phone input components of the whole app.
// So don't change them if you don't need it and override this values through "style" prop instead when using a component.
const stylesParameters = {
    fontSize: 14, // Font size for inputs
    inputHeight: 52, // Height of input elements
    borderWidth: 1, // Border width for components
};

const useStyles = makeStyles({
    // This is default styles for component. If you want to change this styles
    // or add new, use custom styles passed to props or custom classes + stylesheet
    formControl: {
        position: "relative",
        width: "100%",
    },
    label: {
        position: "static",
        color: "#6b7280",
        marginBottom: "5px",

        "& .required-mark": {
            color: "#d32f2f",

            "& svg": {
                display: "inline-block",
                width: stylesParameters.fontSize / 3,
                position: "relative",
                left: "2px",
                bottom: "4px",
            },
        },
    },
    wrapper: {
        display: "flex",
        alignItems: "center",
        border: "1px solid #d1d5db",
        borderRadius: "4px",
        overflow: "hidden",
        backgroundColor: "#ffffff",
        width: "100%",
    },
    dropdown: {
        display: "flex",
        alignItems: "center",
        padding: "0 12px",
        cursor: "pointer",
        borderRight: "1px solid #d1d5db",
        backgroundColor: "#f9f9f9",
        "& .phone-flag": {
            width: "24px",
            height: "16px",
            marginRight: "8px",
        },
        "& .phone-code": {
            fontSize: "1rem",
            color: "#4a5568",
        },
        "& .dropdown-arrow": {
            marginLeft: "8px",
            fontSize: "0.875rem",
            color: "#6b7280",
        },
    },
    dropdownMenu: {
        position: "absolute",
        top: "100%",
        left: "0",
        zIndex: 10,
        width: "100%",
        maxHeight: "200px",
        overflowY: "auto",
        backgroundColor: "#ffffff",
        border: "1px solid #d1d5db",
        borderRadius: "4px",
        marginTop: "4px",
        padding: 0,
        listStyle: "none",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
    },
    dropdownItem: {
        display: "flex",
        alignItems: "center",
        padding: "8px 12px",
        fontSize: "1rem",
        cursor: "pointer",
        transition: "background-color 0.3s",
        "&:hover": {
            backgroundColor: "#f3f4f6",
        },
        "& .phone-flag": {
            width: "24px",
            height: "16px",
            marginRight: "8px",
        },
        "& .phone-code": {
            marginRight: "8px",
            fontSize: "1rem",
            color: "#4a5568",
        },
        "& .phone-name": {
            fontSize: "0.875rem",
            color: "#6b7280",
        },
    },
    input: {
        flex: 1,
        border: "none",
        padding: "12px 24px",
        height: stylesParameters.inputHeight,
        paddingTop: `calc((((${stylesParameters.inputHeight}px - ${stylesParameters.fontSize}px) / 2) - ${stylesParameters.borderWidth}px * 2))`,
        paddingBottom: `calc((((${stylesParameters.inputHeight}px - ${stylesParameters.fontSize}px) / 2) - ${stylesParameters.borderWidth}px * 2))`,
        fontSize: "1rem",
        outline: "none",
        width: "100%",
        "&:disabled": {
            backgroundColor: "#f9f9f9",
        },
    },
});

export default function PhoneInput({
    value,
    onChange, // Uses directly value of component instead of "e.target.value"
    label,
    placeholder,
    helperText,
    className,
    id,
    name,
    style,
    inputStyle,
    labelStyle,
    dropdownStyle,
    disabled,
    isRequired,
}) {
    const classes = useStyles();
    const [selectedCountry, setSelectedCountry] = useState(initialCountry);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [localValue, setLocalValue] = useState("");

    const uniqueId = `phoneInput_${uuidv4()}`; // Generating unique id for input and label using uuid library

    useEffect(() => {
        if (onChange === undefined) {
            console.error(consoleMessages.noOnChangeProvided);
        }
    }, []);

    const handleCountryChange = (country) => {
        setSelectedCountry(country);
        setIsDropdownOpen(false);
    };

    const toggleDropdown = () => {
        if (!disabled) setIsDropdownOpen(!isDropdownOpen);
    };

    const handleInputChange = (e) => {
        if (disabled || e.target.value.length > selectedCountry.phoneLength) return;
        const value = e.target.value.toString().replace(/\D/, "");

        if (onChange) {
            onChange(`${selectedCountry.phone}${value}`);
        } else {
            setLocalValue(value); // Setting local state instead of using external state passed through props
        }
    };

    return (
        <FormControl className={`${classes.formControl} ${className ?? ""}`} style={style}>
            <label className={classes.label} style={labelStyle} htmlFor={id || uniqueId}>
                {label ?? ""}
                {label && isRequired && (
                    <span className="required-mark">
                        <svg
                            stroke="currentColor"
                            fill="currentColor"
                            strokeWidth="0"
                            viewBox="0 0 480 512"
                            className="required-icon"
                            height="1em"
                            width="1em"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path d="M471.99 334.43L336.06 256l135.93-78.43c7.66-4.42 10.28-14.2 5.86-21.86l-32.02-55.43c-4.42-7.65-14.21-10.28-21.87-5.86l-135.93 78.43V16c0-8.84-7.17-16-16.01-16h-64.04c-8.84 0-16.01 7.16-16.01 16v156.86L56.04 94.43c-7.66-4.42-17.45-1.79-21.87 5.86L2.15 155.71c-4.42 7.65-1.8 17.44 5.86 21.86L143.94 256 8.01 334.43c-7.66 4.42-10.28 14.21-5.86 21.86l32.02 55.43c4.42 7.65 14.21 10.27 21.87 5.86l135.93-78.43V496c0 8.84 7.17 16 16.01 16h64.04c8.84 0 16.01-7.16 16.01-16V339.14l135.93 78.43c7.66 4.42 17.45 1.8 21.87-5.86l32.02-55.43c4.42-7.65 1.8-17.43-5.86-21.85z"></path>
                        </svg>
                    </span>
                )}
            </label>
            <div className={classes.wrapper}>
                <div className={classes.dropdown} onClick={toggleDropdown}>
                    <img src={selectedCountry?.image} alt={selectedCountry?.name} className="phone-flag" />
                    <span className="phone-code">{selectedCountry?.phone}</span>
                    <span className="dropdown-arrow">&#9662;</span>
                </div>
                {isDropdownOpen && (
                    <ul className={classes.dropdownMenu} style={dropdownStyle}>
                        {countries.map((country) => (
                            <li
                                key={country?.iso?.["alpha-2"]}
                                className={classes.dropdownItem}
                                onClick={() => handleCountryChange(country)}
                            >
                                <img src={country.image} alt={country.name} className="phone-flag" />
                                <span className="phone-code">{country.phone}</span>
                                <span className="phone-name">{country.name}</span>
                            </li>
                        ))}
                    </ul>
                )}
                <input
                    className={classes.input}
                    type="text"
                    value={value ? value.replace(selectedCountry.phone, "") : localValue}
                    onChange={handleInputChange}
                    placeholder={placeholder}
                    name={name}
                    disabled={disabled}
                    style={inputStyle}
                    id={id || uniqueId}
                />
            </div>
            {helperText && <p style={{ color: "red" }}>{helperText}</p>}
        </FormControl>
    );
}

PhoneInput.propTypes = {
    onChange: PropTypes.func,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};
